<template lang="pug">
  e-input-signature(v-model="signature")
    template(v-slot:activator="{ listeners }")
      div(
        v-if="signatureImage && !canEdit"
        class="dynamic-signature__image"
        :style="signatureImageStyle"
        v-on="listeners"
      )
        img(
          :src="signatureImage"
          :draggable="canEdit"
        )
      div(
        v-else
        class="dynamic-signature-wrapper"
        :draggable="canEdit"
      )
        input(
          v-model="hintValue"
          :readonly="!canEdit"
          ref="signatureInput"
          class="dynamic-signature__input"
          :class="inputClasses"
          :style="inputStyles"
          :placeholder="placeholder"
          v-on="!canEdit && !isDisabled ? listeners : inputListeners"
        )
        v-btn(
          v-if="!xsDevice"
          small
          icon
          class="dynamic-signature__icon"
          @click.stop="handleIconClick"
        )
          e-svg-icon pen-gray
</template>

<script>
import { mapGetters } from 'vuex'
import ESvgIcon from '~/components/elements/icons/e-svg-icon'
import EInputSignature from '~/components/elements/inputs/e-input-signature'
import responsive from '~/mixins/pages/responsive'

export default {
  name: 'TemplatesDynamicSignature',
  components: {
    ESvgIcon,
    EInputSignature
  },
  mixins: [responsive],
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: String,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    fontSize: {
      type: [String, Number],
      default: null
    },
    isResizing: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      hintValue: null,
      signature: null
    }
  },
  computed: {
    ...mapGetters('templatefields', {
      filledFields: 'filledFields'
    }),
    inputStyles() {
      return {
        fontSize: this.xsDevice ? '7px' : this.fontSize + 'px'
      }
    },
    inputClasses() {
      return {
        'error-state': this.hasError
      }
    },
    signatureImage() {
      const prefix = 'data:image/png;base64,'
      const base64 = this.value
      if (this.field.documentFieldValue) {
        return prefix + this.field.documentFieldValue
      } else if (base64) {
        return prefix + base64
      }
      return null
    },
    inputListeners() {
      return {
        focus: this.onFocus,
        blur: () => this.$emit('blur', this.hintValue)
      }
    },
    signatureImageStyle() {
      return {
        width: this.field.width,
        height: this.field.height
      }
    }
  },
  watch: {
    signature(val) {
      if (val !== this.value) {
        this.$emit('update:value', val)
      }
    },
    value(val) {
      this.signature = val
    }
  },
  mounted() {
    this.setSignature()
  },
  methods: {
    onFocus() {
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', this.field.id)
      this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
        id: this.field.id,
        value: true
      })
    },
    handleIconClick() {
      this.$refs.signatureInput.click()
    },
    setSignature() {
      if (this.canEdit) {
        this.hintValue = this.field.hint
      } else if (this.field.documentFieldPreValue) {
        this.signature = this.field.documentFieldPreValue
      } else {
        this.signature = this.field.documentFieldValue
      }
    }
  }
}
</script>

<style scoped lang="scss">
.dynamic-signature {
  &-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }

  &__input {
    width: 100%;
    height: 100%;

    border-radius: 8px;
    border: 1px solid $primary;
    background: rgba(255, 255, 255, 0.7);
    padding: 6px 24px 6px 8px;
    resize: none;
    outline: none;
    font-weight: 300;

    @media (max-width: map-get($breakpoints, 'sm')) {
      padding: 6px 6px 6px 8px;
      border-radius: 4px;
      position: relative;
      top: -8px;
    }

    &::placeholder {
      color: $text-dark;
    }

    &.error-state {
      border: 1px solid $danger;
      background: rgba(255, 23, 68, 0.12);
    }
  }

  &__menu {
    max-height: 400px;
    width: 100%;
    height: 100%;

    position: relative;

    border-radius: 16px;
    background: $white;
    box-shadow: 0 0 40px 0 rgba(0, 0, 0, 0.08);
  }

  &__canvas-container {
    background-color: $background-gray;
  }

  &__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  &__icon {
    width: 16px;
    height: 16px;

    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    z-index: map-get($zIndexes, 'base');

    &::before {
      opacity: 0 !important;
    }
  }

  &__image {
    display: flex;
    align-items: center;
    justify-content: center;

    background:
      linear-gradient(0deg, rgba(143, 37, 125, 0.12) 0%, rgba(143, 37, 125, 0.12)) 100%,
      rgba(255, 255, 255, 0.4);
    border: 1px solid $primary;
    border-radius: 8px;
    cursor: pointer;

    @media (max-width: map-get($breakpoints, 'sm')) {
      border-radius: 4px;
    }

    img {
      max-width: 100%;
      height: auto;
    }
  }
}
</style>
